/**
 * /architecture-decision-record — SEO landing page targeting the
 * "architecture decision record" / "ADR" / "adr template" / "adr example"
 * cluster. Audience skews engineering / technical decision-makers.
 *
 * Differentiation angle: the ADR practice (Michael Nygard, 2011) is good —
 * a small record of *why* an architectural decision was made. The problem
 * is the substrate: ADRs are static markdown in the repo, written once and
 * never re-read, so they drift from reality as the code evolves and become
 * archaeology. Draftlize makes the ADR a living record an AI coding agent
 * (Claude Code / Cursor via MCP) reads AND writes — read before code changes,
 * implementation status flipped to drift_detected when the spec moves.
 *
 * English-only + inline (no i18n), mirroring the comparison pages.
 * Chinese variant lives in ArchitectureDecisionRecordZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ArchitectureDecisionRecord() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/architecture-decision-record"
      documentTitle="Architecture Decision Records (ADRs) That Stay Current · Draftlize"
      documentDescription="A traditional architecture decision record is markdown in the repo — written once, then it atrophies as the code drifts past it. Draftlize makes the ADR a living record an AI coding agent reads and writes, where implementation flips to drift_detected the moment the decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Architecture Decision Records</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Architecture decision records<br/>
            <em>that don't rot in the repo.</em>
          </h1>
          <p className="lede">
            An architecture decision record answers the most expensive question a codebase ever asks: <em>"why is it built this way?"</em> The practice is sound — a small, dated record of the context, the call, and the consequences. The trouble is the substrate. An ADR is a markdown file checked into the repo, written the day the decision is made and never opened again. The code evolves, the decision quietly stops matching reality, and the ADR becomes archaeology. Draftlize keeps the record itself — but makes it something an AI coding agent reads before it touches the code, and one that flips to <em>drift_detected</em> the moment the decision it documents no longer holds.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">A markdown file vs <em>a living ADR.</em></h2>
          </div>
          <p>An ADR in <code>docs/adr/0007-use-event-sourcing.md</code> is correct on the day you commit it and slowly diverges from the running system after that. The fix isn't a stricter review checklist — it's a substrate that knows which records still match the code and tells you when one doesn't.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Markdown ADR in the repo</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>The record itself</td><td className="ours yes">Typed card: context, decision, consequences, dependencies</td><td className="partial">A markdown template, filled by hand</td></tr>
            <tr><td>Status field</td><td className="ours yes">Live — flips to drift_detected on its own</td><td className="no">Hand-edited; usually still says "Accepted"</td></tr>
            <tr><td>Decision changes — does the code know?</td><td className="ours yes">Dependent records flag drift automatically</td><td className="no">No link; the file lies until someone notices</td></tr>
            <tr><td>Can a coding agent use it?</td><td className="ours yes">Native read + write via MCP</td><td className="partial">Reads flat text if you point it there</td></tr>
            <tr><td>Read before the code changes</td><td className="ours yes">The agent pulls the relevant ADRs first</td><td className="no">Nobody opens <code>docs/adr/</code></td></tr>
            <tr><td>Who keeps it honest</td><td className="ours yes">The substrate tracks it</td><td className="no">A review checklist nobody enforces</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why ADRs <em>atrophy.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Written once, read never</h3>
            <p>Michael Nygard proposed the architecture decision record in 2011 to capture <em>why</em> — the context and the trade-offs behind a structural call. The format is right. But an ADR is filed in <code>docs/adr/</code> and the audience is a future engineer who never opens the folder. A record nobody reads back can't do the one job it has.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The code drifts, the record doesn't move with it</h3>
            <p>ADR-0007 says "we use event sourcing for the ledger." Eighteen months later half the ledger is CRUD again, decided in three pull requests and a Slack thread. The status still reads <em>Accepted</em>. The record now actively misleads the next person who trusts it — which is worse than no record at all.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Keeping it current loses to shipping</h3>
            <p>Updating an ADR is a manual chore that competes with merging the PR, and the PR always wins. So the records freeze at the moment of writing while the system keeps moving, and the gap between <code>docs/adr/</code> and <code>main</code> widens every sprint.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">An <em>ADR template</em>, made self-maintaining.</h2>
        </div>
        <p style={{ maxWidth: 720, color: 'var(--ink-1)', fontSize: 16, lineHeight: 1.65, margin: '0 0 32px' }}>
          A standard ADR has five fields. Draftlize keeps all five — and turns the one that always lies, <em>Status</em>, into something the substrate maintains instead of something you forget to edit.
        </p>
        <div className="problem-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>TITLE</span>
            <h3 className="serif" style={{ fontSize: 19 }}>What was decided</h3>
            <p>A short, addressable name. In Draftlize it's a card with a stable ID, citable as <em>"per ADR-0007"</em> from a spec, a thread, or an agent — a real link, not a filename you have to grep for.</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>STATUS</span>
            <h3 className="serif" style={{ fontSize: 19 }}>The field that lies</h3>
            <p>In markdown, Status is hand-edited and stays <em>Accepted</em> forever. In Draftlize it's live: when an upstream decision moves, the implementation flips itself to <em>drift_detected</em>, so the record tells the truth without anyone updating it.</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>CONTEXT</span>
            <h3 className="serif" style={{ fontSize: 19 }}>Why it came up</h3>
            <p>The forces in play when the call was made. Linked to the decisions it depends on, so when one of <em>those</em> changes, this record knows its premises shifted.</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>DECISION</span>
            <h3 className="serif" style={{ fontSize: 19 }}>The call, and the rejected paths</h3>
            <p>What you chose and what you ruled out. The alternatives are part of the card — so six months later nobody re-litigates an option you already closed.</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>CONSEQUENCES</span>
            <h3 className="serif" style={{ fontSize: 19 }}>What it commits you to</h3>
            <p>What follows from the decision, good and bad. The dependent cards that inherit those consequences are linked — and they flag drift when this record changes underneath them.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">When the reader is <em>a coding agent.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It reads the ADRs before it writes code</h3>
            <p>Claude Code or Cursor, connected over MCP, pulls the relevant records before touching a module — so the decision behind "we use event sourcing here" is in context at the exact moment it matters, not buried in a folder nobody opens. The reader who never came back is now the reader who never skips a card.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">It writes them back too</h3>
            <p>When the agent makes a structural call mid-task, it can record a new ADR as a card — title, context, decision, consequences — without leaving the work to open a markdown file. The record gets written because writing it is one tool call, not a context switch.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Drift surfaces instead of hiding</h3>
            <p>Each record carries explicit dependencies. Change an upstream decision and every record built on it flips to <em>drift_detected</em> — the same way a build system invalidates everything downstream of a changed file. The gap between the decision and the code stops being something you discover in a post-mortem.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          The ADR practice was never the problem. The problem is a record that can't tell when it's already wrong.
        </blockquote>
        <cite>Keep the five fields. Move them onto a substrate that maintains the one that lies.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is an architecture decision record (ADR)?', a: 'An ADR is a short, dated record of one significant architectural decision — the context, the call, and the consequences. The practice was proposed by Michael Nygard in 2011 to capture why a system is built the way it is, so future engineers do not have to reverse-engineer the reasoning from the code.' },
            { q: 'What is in a standard ADR template?', a: 'Five fields: title, status, context, decision, and consequences. Title names the call, status tracks whether it is proposed, accepted, or superseded, context records the forces in play, decision states what was chosen and rejected, and consequences captures what it commits you to.' },
            { q: 'Why do ADRs go out of date?', a: 'Because they are static markdown checked into the repo, written once and rarely reopened. As the code evolves the decision quietly stops matching reality, but the status field still reads "Accepted" — so the record actively misleads the next person who trusts it, which is worse than no record at all.' },
            { q: 'How does Draftlize keep ADRs from drifting?', a: 'Draftlize keeps the same five fields but makes the record a card an AI coding agent reads before it touches the code and writes back when it makes a call. When an upstream decision moves, the implementation status flips itself to drift_detected — so the one field that always lies, status, stays honest without anyone editing it.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Start your ADRs <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Spin up a project, port your existing ADRs in as cards, and connect your coding agent over MCP — so the records get read before the code changes, and flag drift the moment a decision moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="decision-log" />
    </MarketingShell>
  );
}
